import { useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTargets } from "./Targets/TargetsContext";
import Filter from "../../components/Filter/Filter.js";
import Dropdown from "../../components/Dropdown/Dropdown.js";
import TargetsView, { buildSessions } from "./Targets/TargetsView.js";
import { useYear } from "../../context/YearContext.js";
import {
  PRELIM_DATES,
  TARGET_STAGES,
  dateLabel,
  hasPrelimDateSelect,
  stageDate,
} from "../../utils/archeryConfig.js";
import StatusMessage from "../../components/StatusMessage/StatusMessage.js";

const DistanceView = () => {
  const { distanceParam } = useParams();
  const distance = distanceParam.replace("distance", "");
  const { t, i18n } = useTranslation();
  const { year } = useYear();
  const {
    timeSlots,
    dates,
    selectedDate,
    selectedStage,
    loading,
    error,
    fetchRounds,
  } = useTargets();
  const isPrelim = selectedStage === "PRE";
  const [filters, setFilters] = useState({});

  useEffect(() => {
    fetchRounds(distance);
  }, [fetchRounds, distance]);

  // 자리·먼저/다음은 검색 전 전체 선수로 계산하고, 검색은 표시만 거름
  const sessions = useMemo(
    () => buildSessions(timeSlots, distance, year, !isPrelim),
    [timeSlots, distance, year, isPrelim]
  );

  const matches = (p) =>
    (!filters.country || p.country === filters.country) &&
    (!filters.playerName ||
      p.name.toLowerCase().includes(filters.playerName.toLowerCase()));

  const filteredSessions = sessions.map((session) => ({
    ...session,
    groups: session.groups.map((group) => ({
      ...group,
      players: group.players.filter(matches),
    })),
  }));

  // 경기 선택 (검색 줄 맨 앞). 본선은 단계마다 경기일이 하루라 날짜 자동
  const stageLabels = TARGET_STAGES.map((stage) => t(`targets.stage-${stage}`));
  const stageDropdown = (
    <Dropdown
      props={{
        data: stageLabels,
        value: t(`targets.stage-${selectedStage}`),
        onChange: (label) => {
          const stage = TARGET_STAGES[stageLabels.indexOf(label)];
          fetchRounds(
            distance,
            stage === "PRE" ? PRELIM_DATES[year]?.[0] : stageDate(year, stage),
            stage
          );
        },
      }}
    />
  );

  // 경기 날짜 선택 (경기 선택 다음): 예선을 여러 날 하는 종목만 (hasPrelimDateSelect)
  const labels = dates.map((date) => dateLabel(date, i18n.language));
  const dateDropdown =
    isPrelim && hasPrelimDateSelect(year, distance) && selectedDate && (
    <Dropdown
      props={{
        data: labels,
        value: dateLabel(selectedDate, i18n.language),
        onChange: (label) =>
          fetchRounds(distance, dates[labels.indexOf(label)]),
      }}
    />
  );

  // 불러오는 동안에도 검색 줄은 그대로 둠 (입력한 검색어와 걸러진 결과가 어긋나지 않게)
  let content;
  if (loading) {
    content = <StatusMessage type="loading" />;
  } else if (error) {
    content = <StatusMessage type="error" description={error} />;
  } else if (!timeSlots || timeSlots.length === 0) {
    content = selectedDate ? (
      <StatusMessage
        title={t("targets.no-match", {
          date: dateLabel(selectedDate, i18n.language),
          distance,
        })}
        description={t("status.no-match-desc")}
      />
    ) : (
      <StatusMessage />
    );
  } else if (
    filteredSessions.every((session) =>
      session.groups.every((group) => group.players.length === 0)
    )
  ) {
    content = <StatusMessage type="search" />;
  } else {
    content = filteredSessions.map((session) => (
      <TargetsView
        key={`${session.times[0]}${session.gender}`}
        event={distance}
        year={year}
        session={session}
      />
    ));
  }

  return (
    <>
      <Filter
        onSearch={setFilters}
        leading={
          <>
            {stageDropdown}
            {dateDropdown}
          </>
        }
      />
      {content}
    </>
  );
};

export default DistanceView;
